/**
 * SEO utility — updates document title, meta description, canonical, and OG tags
 * per page and per language. No external dependencies required.
 */

const BASE_URL = 'https://www.nexuni.com';

const PAGE_SEO = {
  home: {
    title: {
      zh: 'Nexuni 耐思尼 | AI 機器人自動化專家',
      en: 'Nexuni | AI Robots for Automation',
      ja: 'Nexuni | AI ロボット自動化ソリューション'
    },
    description: {
      zh: 'Nexuni 耐思尼致力於 AI 機器人落地應用，提供智慧焊接、保全巡邏、設施管理、停車管理等自動化解決方案。',
      en: 'Nexuni develops AI-powered robots for real-world automation — welding, security patrol, facility management, and parking systems.',
      ja: 'Nexuni は AI ロボットの実用化に取り組み、溶接、警備巡回、施設管理、駐車場管理などの自動化ソリューションを提供。'
    }
  },
  solutions: {
    title: {
      zh: 'AI 機器人產品 | Nexuni 耐思尼',
      en: 'AI Robot Products & Solutions | Nexuni',
      ja: 'AI ロボット製品 | Nexuni'
    },
    description: {
      zh: '探索 Nexuni 全系列 AI 機器人產品：智慧焊接機器人、保全巡邏機器狗、高樓清潔機器人、車牌辨識停車管理系統、AI 影像保全系統。',
      en: 'Explore Nexuni AI robot products: smart welding robots, security patrol dogs, facade cleaning robots, LPR parking management systems, and AI video security.',
      ja: 'Nexuni の AI ロボット製品：スマート溶接ロボット、警備巡回ロボットドッグ、外壁清掃ロボット、車番認識駐車管理システム、AI 映像セキュリティ。'
    }
  },
  about_us: {
    title: {
      zh: '關於我們 | Nexuni 耐思尼 — AI 機器人公司',
      en: 'About Us | Nexuni — AI Robotics Company',
      ja: '会社概要 | Nexuni — AI ロボット企業'
    },
    description: {
      zh: 'Nexuni 成立於新竹科學園區，掌握 AI 機器人完整核心技術，團隊來自 MIT、CMU、台大、清大等頂尖學府，專注於解決產業缺工與高危險作業挑戰。',
      en: 'Nexuni, founded in Hsinchu Science Park, owns the full AI robotics technology stack. Our team from MIT, CMU, NTU builds robots that solve labor shortages in facility management, manufacturing, and security.',
      ja: 'Nexuni は台湾・新竹サイエンスパークで設立。MIT、CMU、台湾大学出身のチームが AI ロボットの完全な技術スタックを保有し、施設管理・製造・警備の人手不足を解決。'
    }
  },
  core_technology: {
    title: {
      zh: '核心技術 | Nexuni AI 機器人平台',
      en: 'Core Technology | Nexuni AI Robot Platform',
      ja: 'コア技術 | Nexuni AI ロボットプラットフォーム'
    },
    description: {
      zh: 'Nexuni 自研 AI 機器人核心平台，整合感知、控制、SLAM 定位與決策，採用強化學習實現四足與人形機器人運動控制，端到端 VLA 模型進行機械手臂操作，並透過人類示範學習策略，支援多場域彈性部署。',
      en: 'Nexuni proprietary AI robotics platform integrating perception, control, and SLAM. Reinforcement learning for quadruped and humanoid locomotion, end-to-end VLA for robot manipulation, and policy learning from human demonstration. Flexible deployment across facility management and industrial automation.',
      ja: 'Nexuni 独自の AI ロボットプラットフォーム。知覚・制御・SLAM を統合。強化学習による四足・ヒューマノイドの歩行制御、エンドツーエンド VLA によるロボットマニピュレーション、人間のデモンストレーションからの方策学習。施設管理・産業自動化に柔軟展開可能。'
    }
  },
  colab_flow: {
    title: {
      zh: '合作流程 | Nexuni 耐思尼 — 標準合作與首發客戶方案',
      en: 'How to Collaborate | Nexuni — Standard & Launch Customer Programs',
      ja: '協力プロセス | Nexuni — 標準導入 & ローンチカスタマー'
    },
    description: {
      zh: '兩種合作模式：標準合作 — 從產品型錄選擇現成方案，四步驟快速部署；首發客戶 — Nexuni 派駐工程師至現場，從零打造專屬機器人與自動化產品。',
      en: 'Two ways to work with Nexuni: Standard Collaboration — choose from our catalog and deploy in four steps; Launch Customer — we send engineers to your site and build a custom robot or automation solution from scratch.',
      ja: '2つの協力モデル：標準導入 — カタログから選んで4ステップで導入；ローンチカスタマー — エンジニアを現場に派遣し、専用ロボット・自動化ソリューションをゼロから開発。'
    }
  },
  career: {
    title: {
      zh: '加入我們 | Nexuni 耐思尼徵才',
      en: 'Careers at Nexuni | AI Robotics Jobs in Taiwan',
      ja: '採用情報 | Nexuni — AI ロボットエンジニア募集'
    },
    description: {
      zh: 'Nexuni 徵求機器人軟體工程師、AI 軟體工程師、硬體設計工程師。加入我們打造真正落地的 AI 機器人。',
      en: 'Join Nexuni — hiring Robot Software Engineers (ROS2/SLAM), AI Software Engineers, and Hardware Engineers. Build AI robots that work in the real world.',
      ja: 'Nexuni 採用中 — ロボットソフトウェアエンジニア（ROS2/SLAM）、AI ソフトウェアエンジニア、ハードウェアエンジニア。現場で稼働する AI ロボットを開発。'
    }
  },
  contact: {
    title: {
      zh: '聯絡我們 | Nexuni 耐思尼',
      en: 'Contact Us | Nexuni — AI Robot Solutions',
      ja: 'お問い合わせ | Nexuni'
    },
    description: {
      zh: '聯絡 Nexuni 耐思尼，了解 AI 機器人自動化解決方案。新竹科學園區，服務台灣及亞太地區。',
      en: 'Contact Nexuni for AI robot automation solutions. Based in Hsinchu Science Park, Taiwan.',
      ja: 'Nexuni へのお問い合わせ。AI ロボット自動化ソリューション。台湾・新竹サイエンスパーク拠点。'
    }
  },
  stories: {
    title: {
      zh: '成功案例 | Nexuni 耐思尼',
      en: 'Case Studies | Nexuni AI Robots',
      ja: '導入事例 | Nexuni AI ロボット'
    },
    description: {
      zh: 'Nexuni AI 機器人成功案例：智慧停車場、自動化焊接、設施管理等真實導入成果。',
      en: 'Nexuni AI robot case studies: smart parking, automated welding, facility management, and real-world deployment results.',
      ja: 'Nexuni AI ロボット導入事例：スマート駐車場、自動溶接、施設管理などの実績。'
    }
  },
  blog: {
    title: {
      zh: '最新消息 | Nexuni 耐思尼',
      en: 'News & Blog | Nexuni',
      ja: 'ニュース | Nexuni'
    },
    description: {
      zh: 'Nexuni 最新消息、產品更新與 AI 機器人產業趨勢。',
      en: 'Latest news, product updates, and AI robotics industry insights from Nexuni.',
      ja: 'Nexuni の最新ニュース、製品アップデート、AI ロボット業界動向。'
    }
  },
  environment: {
    title: {
      zh: '文化與環境 | Nexuni 耐思尼',
      en: 'Life at Nexuni | Culture & Environment',
      ja: '企業文化 | Nexuni'
    },
    description: {
      zh: '了解 Nexuni 的企業文化：創新、實務學習、共同成長，打造舒適且激勵人心的工作環境。',
      en: 'Discover Nexuni culture: innovation, hands-on learning, and growth. A comfortable and empowering work environment.',
      ja: 'Nexuni の企業文化：革新、実践学習、共に成長する、快適で力を与える職場環境。'
    }
  }
};

const PRODUCT_SEO = {
  collie: {
    title: {
      zh: 'Collie AI 保全巡邏機器狗 | Nexuni',
      en: 'Collie AI Security Patrol Robot Dog | Nexuni',
      ja: 'Collie AI 警備巡回ロボットドッグ | Nexuni'
    },
    description: {
      zh: 'Nexuni Collie AI 保全巡邏機器狗，自主導航巡邏、即時異常偵測與告警，適用於工廠、社區、園區等設施管理場域。',
      en: 'Nexuni Collie AI security patrol robot dog with autonomous navigation, real-time anomaly detection, and alerts. Ideal for facility management in factories, campuses, and communities.',
      ja: 'Nexuni Collie AI 警備巡回ロボットドッグ。自律ナビゲーション、リアルタイム異常検知、アラート機能。工場・キャンパス・コミュニティの施設管理に最適。'
    }
  },
  beaver: {
    title: {
      zh: 'Beaver 智慧焊接機器人 | Nexuni',
      en: 'Beaver Smart Welding Robot | Nexuni',
      ja: 'Beaver スマート溶接ロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni Beaver 智慧焊接機器人結合雷射掃描與協作機械手臂，自動修正工件誤差，設定時間減少 80%，生產效率提升 50%。',
      en: 'Nexuni Beaver smart welding robot combines laser scanning with collaborative arms. Auto-corrects workpiece deviations, reduces setup time by 80%, increases efficiency by 50%.',
      ja: 'Nexuni Beaver スマート溶接ロボット。レーザースキャンと協働アームを統合、ワーク誤差を自動補正、段取り時間 80% 削減、生産効率 50% 向上。'
    }
  },
  spider: {
    title: {
      zh: 'Spider 高樓外牆清洗機器人 | Nexuni',
      en: 'Spider High-Rise Facade Cleaning Robot | Nexuni',
      ja: 'Spider 高層外壁清掃ロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni Spider 高樓外牆清洗機器人，自動化高危險清潔作業，提升建築設施管理安全與效率。',
      en: 'Nexuni Spider high-rise facade cleaning robot automates dangerous cleaning tasks, improving safety and efficiency in building facility management.',
      ja: 'Nexuni Spider 高層外壁清掃ロボット。危険な清掃作業を自動化し、ビル施設管理の安全性と効率を向上。'
    }
  },
  elephant: {
    title: {
      zh: 'AI 雙臂式服務型機器人 | Nexuni',
      en: 'AI Bimanual Service Robot | Nexuni',
      ja: 'AI 双腕サービスロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 雙臂式服務型機器人，以端到端人類示範策略學習為核心，少量示範即可學會新任務，適用於工廠、倉儲、餐飲與服務業。',
      en: 'Nexuni AI bimanual service robot — end-to-end policy learning from human demonstration, learns new tasks from minimal demos. Ideal for factories, warehouses, food & beverage, and service industries.',
      ja: 'Nexuni AI 双腕サービスロボット。人間のデモからのエンドツーエンド方策学習で少数デモから新タスクを習得。工場・倉庫・飲食業・サービス業に最適。'
    }
  },
  pms100: {
    title: {
      zh: '車牌辨識停車管理系統 PMS-100 | Nexuni',
      en: 'LPR Parking Management System PMS-100 | Nexuni',
      ja: '車番認識駐車管理システム PMS-100 | Nexuni'
    },
    description: {
      zh: 'Nexuni PMS-100 標準化車牌辨識停車管理系統，支援現金、信用卡、QR-CODE 支付，適用各類停車場智慧管理。',
      en: 'Nexuni PMS-100 LPR-based parking management system with cash, credit card, and QR code payment. Smart parking automation for any facility.',
      ja: 'Nexuni PMS-100 車番認識駐車管理システム。現金・クレジットカード・QR コード決済対応。あらゆる施設のスマート駐車場管理。'
    }
  },
  pms100m: {
    title: {
      zh: '社區車牌辨識停車管理主機 | Nexuni',
      en: 'Community LPR Parking Control Unit | Nexuni',
      ja: 'コミュニティ車番認識駐車管理ユニット | Nexuni'
    },
    description: {
      zh: 'Nexuni 社區車牌辨識停車管理主機，AI 車牌辨識、訪客管理、出入口控制三合一，體積小巧免汰換現有設備。',
      en: 'Nexuni community LPR parking control unit — compact AI license plate recognition, visitor management, and access control in one device. No infrastructure replacement needed.',
      ja: 'Nexuni コミュニティ車番認識駐車管理ユニット。コンパクトな AI 車番認識、訪問者管理、入退場制御を一体化。既存設備の交換不要。'
    }
  },
  pms100le: {
    title: {
      zh: '車牌辨識主機 PMS-100LE | Nexuni',
      en: 'LPR Engine PMS-100LE | Nexuni',
      ja: '車番認識エンジン PMS-100LE | Nexuni'
    },
    description: {
      zh: 'Nexuni PMS-100LE 車牌辨識主機，體積小於手機，低功耗 AI 邊緣運算，開放式 API 架構，輕鬆串接各類停車管理系統。',
      en: 'Nexuni PMS-100LE LPR engine — smaller than a phone, low-power AI edge computing, open API architecture for seamless parking system integration.',
      ja: 'Nexuni PMS-100LE 車番認識エンジン。スマホより小型、低消費電力 AI エッジ推論、オープン API で既存駐車システムに柔軟接続。'
    }
  },
  vpgs100: {
    title: {
      zh: '智慧影像車位導引系統 | Nexuni',
      en: 'Vision-Based Parking Guidance System | Nexuni',
      ja: '映像式駐車誘導システム | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧影像車位導引系統，AI 即時偵測空位並引導車輛，提升停車場營運效率與使用者體驗。',
      en: 'Nexuni vision-based parking guidance system uses AI to detect available spaces in real time, improving parking facility operations and user experience.',
      ja: 'Nexuni 映像式駐車誘導システム。AI がリアルタイムで空き状況を検知し、駐車場運営効率とユーザー体験を向上。'
    }
  },
  vs100: {
    title: {
      zh: 'AI 智慧影像保全系統 VS-100 | Nexuni',
      en: 'AI Video Security System VS-100 | Nexuni',
      ja: 'AI 映像セキュリティシステム VS-100 | Nexuni'
    },
    description: {
      zh: 'Nexuni VS-100 AI 智慧影像保全系統，多路攝影機即時監控、異常告警與智慧回放搜尋，大幅降低人力監看需求。',
      en: 'Nexuni VS-100 AI video security system with multi-camera real-time monitoring, anomaly alerts, and smart playback search. Reduces manual surveillance needs for facility management.',
      ja: 'Nexuni VS-100 AI 映像セキュリティシステム。マルチカメラのリアルタイム監視、異常アラート、スマート再生検索で人的監視を大幅削減。'
    }
  },
  ilp100: {
    title: {
      zh: 'AI 違停辨識系統 ILP-100 | Nexuni',
      en: 'AI Illegal Parking Detection System ILP-100 | Nexuni',
      ja: 'AI 違法駐車検知システム ILP-100 | Nexuni'
    },
    description: {
      zh: 'Nexuni ILP-100 AI 違停辨識系統，結合車牌辨識與 RF 定位技術，自動偵測違規停車並即時告警。',
      en: 'Nexuni ILP-100 AI illegal parking detection system combining LPR and RF positioning for automated violation detection and real-time alerts.',
      ja: 'Nexuni ILP-100 AI 違法駐車検知システム。車番認識と RF 測位を組み合わせた自動違反検知とリアルタイムアラート。'
    }
  },
  pp100: {
    title: {
      zh: '智慧停車柱 PP-100 | Nexuni',
      en: 'Intelligent Parking Pole PP-100 | Nexuni',
      ja: 'スマート駐車ポール PP-100 | Nexuni'
    },
    description: {
      zh: 'Nexuni PP-100 智慧停車柱，搭載低功耗 AI 車牌辨識模組，可替換式電池設計，適合無電力供應場域。',
      en: 'Nexuni PP-100 intelligent parking pole with low-power AI LPR module and replaceable battery design. Ideal for locations without stable power supply.',
      ja: 'Nexuni PP-100 スマート駐車ポール。低消費電力 AI 車番認識モジュール搭載、交換式バッテリー設計。電源のない場所に最適。'
    }
  }
};

/**
 * Update page SEO: title, description, canonical, OG tags
 */
function updateSEO(pageKey, lang, path) {
  const l = lang === 'zh-tw' ? 'zh' : lang;
  const seo = PAGE_SEO[pageKey] || PAGE_SEO['home'];

  // Title
  document.title = seo.title[l] || seo.title['zh'];

  // Meta description
  setMeta('description', seo.description[l] || seo.description['zh']);

  // Canonical
  const canonical = BASE_URL + (path || '/' + lang + '/');
  setLink('canonical', canonical);

  // Open Graph
  setMeta('og:title', document.title, 'property');
  setMeta('og:description', seo.description[l] || seo.description['zh'], 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:locale', l === 'zh' ? 'zh_TW' : (l === 'ja' ? 'ja_JP' : 'en_US'), 'property');

  // Twitter
  setMeta('twitter:title', document.title);
  setMeta('twitter:description', seo.description[l] || seo.description['zh']);

  // hreflang
  updateHreflang(path ? path.replace(/^\/(zh-tw|en|ja)/, '') : '/');
}

/**
 * Update SEO for product/tech pages
 */
function updateProductSEO(productId, lang, path) {
  const l = lang === 'zh-tw' ? 'zh' : lang;
  const seo = PRODUCT_SEO[productId];

  if (!seo) {
    updateSEO('solutions', lang, path);
    return;
  }

  document.title = seo.title[l] || seo.title['zh'];
  setMeta('description', seo.description[l] || seo.description['zh']);

  const canonical = BASE_URL + (path || '/' + lang + '/tech/' + productId);
  setLink('canonical', canonical);

  setMeta('og:title', document.title, 'property');
  setMeta('og:description', seo.description[l] || seo.description['zh'], 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:locale', l === 'zh' ? 'zh_TW' : (l === 'ja' ? 'ja_JP' : 'en_US'), 'property');

  setMeta('twitter:title', document.title);
  setMeta('twitter:description', seo.description[l] || seo.description['zh']);

  updateHreflang('/tech/' + productId);
}

// --- Helpers ---

function setMeta(name, content, attr) {
  attr = attr || 'name';
  var el = document.querySelector('meta[' + attr + '="' + name + '"]');
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

function setLink(rel, href) {
  var el = document.querySelector('link[rel="' + rel + '"]');
  if (el) {
    el.setAttribute('href', href);
  }
}

function updateHreflang(pagePath) {
  var langs = { 'zh-TW': 'zh-tw', 'en': 'en', 'ja': 'ja' };
  Object.keys(langs).forEach(function(hreflang) {
    var el = document.querySelector('link[hreflang="' + hreflang + '"]');
    if (el) {
      el.setAttribute('href', BASE_URL + '/' + langs[hreflang] + pagePath);
    }
  });
  var def = document.querySelector('link[hreflang="x-default"]');
  if (def) {
    def.setAttribute('href', BASE_URL + '/zh-tw' + pagePath);
  }
}

export { updateSEO, updateProductSEO };
